/**
 * ErrorBoundary Component
 *
 * Global error boundary for catching JavaScript errors anywhere in the component tree.
 * Provides a fallback UI and recovery options when errors occur.
 *
 * @see https://react.dev/reference/react/Component#catching-rendering-errors-with-an-error-boundary
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import i18next from 'i18next';
import {
  Container,
  Paper,
  Stack,
  Text,
  Button,
  Group,
  ThemeIcon,
  Code,
  Collapse,
  Box,
  Center
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconRefresh,
  IconHome,
  IconChevronDown,
  IconChevronUp,
  IconBug
} from '@tabler/icons-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback component to render on error */
  fallback?: ReactNode;
  /** Called when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Whether to show error details (default: true in development) */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showErrorDetails: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <App />
 * </ErrorBoundary>
 * ```
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleRetry = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false
    });
  };

  handleGoHome = (): void => {
    // Reset error state and navigate to home
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showErrorDetails: false
    });
    // Use hash router navigation
    window.location.hash = '#/dashboard';
  };

  toggleErrorDetails = (): void => {
    this.setState(prev => ({ showErrorDetails: !prev.showErrorDetails }));
  };

  render(): ReactNode {
    const { hasError, error, errorInfo, showErrorDetails } = this.state;
    const { children, fallback, showDetails = process.env.NODE_ENV === 'development' } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return fallback;
      }

      // Default fallback UI
      return (
        <Box
          style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Container size="sm">
            <Paper
              shadow="xl"
              p="xl"
              radius="lg"
              style={{
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Stack gap="lg" align="center">
                {/* Error Icon */}
                <ThemeIcon
                  size={80}
                  radius="xl"
                  variant="gradient"
                  gradient={{ from: 'red', to: 'orange', deg: 45 }}
                >
                  <IconAlertTriangle size={40} />
                </ThemeIcon>

                {/* Error Title */}
                <Stack gap="xs" align="center">
                  <Text size="xl" fw={700} ta="center">
                    {i18next.t('common:errorBoundary.title')}
                  </Text>
                  <Text c="dimmed" ta="center" size="sm">
                    {i18next.t('common:errorBoundary.description')}
                  </Text>
                </Stack>

                {/* Action Buttons */}
                <Group justify="center" gap="md">
                  <Button
                    variant="gradient"
                    gradient={{ from: '#2F3289', to: '#40266A', deg: 45 }}
                    leftSection={<IconRefresh size={18} />}
                    onClick={this.handleRetry}
                  >
                    {i18next.t('common:errorBoundary.retry')}
                  </Button>
                  <Button
                    variant="outline"
                    color="gray"
                    leftSection={<IconHome size={18} />}
                    onClick={this.handleGoHome}
                  >
                    {i18next.t('common:errorBoundary.goToDashboard')}
                  </Button>
                </Group>

                {/* Error Details (Development Mode) */}
                {showDetails && error && (
                  <Box w="100%" mt="md">
                    <Button
                      variant="subtle"
                      color="gray"
                      size="xs"
                      fullWidth
                      onClick={this.toggleErrorDetails}
                      rightSection={
                        showErrorDetails ? (
                          <IconChevronUp size={14} />
                        ) : (
                          <IconChevronDown size={14} />
                        )
                      }
                      leftSection={<IconBug size={14} />}
                    >
                      {showErrorDetails ? i18next.t('common:errorBoundary.hideDetails') : i18next.t('common:errorBoundary.showDetails')}
                    </Button>

                    <Collapse in={showErrorDetails}>
                      <Paper
                        p="md"
                        mt="sm"
                        radius="md"
                        style={{
                          background: 'rgba(0, 0, 0, 0.03)',
                          border: '1px solid rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <Stack gap="sm">
                          <Box>
                            <Text size="xs" fw={600} c="dimmed" mb={4}>
                              {i18next.t('common:errorBoundary.errorLabel')}
                            </Text>
                            <Code
                              block
                              style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                fontSize: '11px',
                                maxHeight: '100px',
                                overflow: 'auto'
                              }}
                            >
                              {error.toString()}
                            </Code>
                          </Box>

                          {errorInfo?.componentStack && (
                            <Box>
                              <Text size="xs" fw={600} c="dimmed" mb={4}>
                                {i18next.t('common:errorBoundary.componentStack')}
                              </Text>
                              <Code
                                block
                                style={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  fontSize: '10px',
                                  maxHeight: '150px',
                                  overflow: 'auto'
                                }}
                              >
                                {errorInfo.componentStack}
                              </Code>
                            </Box>
                          )}
                        </Stack>
                      </Paper>
                    </Collapse>
                  </Box>
                )}

                {/* Footer */}
                <Text size="xs" c="dimmed" ta="center" mt="md">
                  {i18next.t('common:errorBoundary.persistentError')}
                </Text>
              </Stack>
            </Paper>
          </Container>
        </Box>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
